import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { CLIENT_RETURN_TO_KEY, CLIENT_PLACED_DECOY_KEY } from '../lib/sessionKeys'
import { LoginPage } from '../pages/LoginPage'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRole?: 'analyst' | 'admin'
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { principal, isAnalyst, isAdmin, checkAuth } = useAuthStore()
  const location = useLocation()

  const isAuthenticated = checkAuth()

  useEffect(() => {
    if (isAuthenticated && !principal) {
      useAuthStore.getState().restoreSession()
    }
  }, [isAuthenticated, principal])

  // Not authenticated — redirect to login. router state.from is lost the
  // moment the tab is already on "/" (refresh, later sign-in, CDP re-open),
  // so a client bounce also persists to sessionStorage — never a staff door,
  // and never a token. Closing the tab drops sessionStorage too, so an exact
  // /client bounce (a client came to set a tripwire) also lands in
  // localStorage, which survives past the tab.
  if (!isAuthenticated) {
    if (location.pathname.startsWith('/client')) {
      const returnTo = location.pathname + location.search
      sessionStorage.setItem(CLIENT_RETURN_TO_KEY, returnTo)
      if (location.pathname === '/client') {
        localStorage.setItem(CLIENT_RETURN_TO_KEY, returnTo)
      }
    }
    // Exact /client is the client's front door to place a tripwire — bouncing
    // through "/" here stranded them at a first-meeting door that never
    // mounts Place (DW-CLIENT-PLACE-STAYS-ON-CLIENT). Render the sign-in card
    // in place instead, so the address bar (and any bookmark) stays on
    // /client. Every other /client/* subpath keeps today's bounce to "/".
    if (location.pathname === '/client') {
      // completeLogin already placed the tripwire and handed off its result
      // before authLogin's getMe() 401 wiped dw_token (DW-CLIENT-PLACE-DECOY-
      // SURVIVES-GETME-401). Without this, checkAuth() goes false right after
      // a successful place and this door re-renders LoginPage, stranding the
      // client behind the password door they just cleared. Paint children so
      // ClientDashboard's own place-handoff gate can show the decoy sheet
      // instead. Do not mint a session or call placeTripwire here.
      //
      // A marker is not a session (DW-CLIENT-PLACE-NO-BEARER-IS-THE-DOOR):
      // CLIENT_PLACE_ERROR_KEY / CLIENT_PLACE_HANDOFF_KEY are tab-sticky
      // gates, not proof of a Bearer — a getMe 401 that wipes dw_token can
      // leave either behind with no session ever minted, and painting
      // children for them alone hides LoginPage behind a dead error sheet the
      // client can never clear (placeFirst refuses to POST without a
      // Bearer). Only the one-time decoy payload itself — the client already
      // has the secret in hand — is worth painting children for with no
      // Bearer. Handoff/error alone fall through to LoginPage so the client
      // can sign in again and Try again can actually POST.
      if (sessionStorage.getItem(CLIENT_PLACED_DECOY_KEY)) {
        return <>{children}</>
      }
      return <LoginPage />
    }
    return <Navigate to="/" state={{ from: location }} replace />
  }

  // Session being restored — show minimal loading. Exact /client is the
  // client's front door for a JWT tripwire place (DW-CLIENT-PLACE-PAINTS-
  // WITHOUT-PRINCIPAL): a slow/failed getMe must not trap the client who
  // just placed a decoy behind an endless spinner — fall through to render
  // children so the dashboard's own place-handoff gate can paint the card.
  // Every staff door (any requiredRole, or any other path) keeps the wait.
  if (isAuthenticated && !principal && !(location.pathname === '/client' && !requiredRole)) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-brass border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Getting ready…</p>
        </div>
      </div>
    )
  }

  // Role check. Only real staff fall back to a staff console — a client
  // (including an org admin) who misses a staff door never paints staff
  // children and never reaches /analyst.
  //
  // Where the client lands is the product call (DW-CLIENT-HOME-PROTECTED
  // ROUTE-STAFF-FALLBACK): a household that hits /analyst or /admin from a
  // bookmark, a leftover link, or the wrong door used to be replace-navigated
  // to /client/interview — an incomplete first meeting that never mounts
  // HomeTripwiresCard, so the Word decoy download is off the picture the
  // whole time they are in it. DigitalWatch.md: canaries are in the same
  // picture — a tripwire the analyst sees with the logs, not a /client
  // gimmick the model never reads. So a client (including an org admin) who
  // misses a staff door goes to /client home, where the holding picture and
  // Download Decoy (.docx) live. Do not start a meeting here.
  if (requiredRole === 'admin' && !isAdmin) {
    return isAnalyst ? (
      <Navigate to="/analyst" replace />
    ) : (
      <Navigate to="/client" replace />
    )
  }

  // Admin is a superset of analyst: the human operator (admin) must be able to
  // reach the analyst console + escalation view.
  if (requiredRole === 'analyst' && !isAnalyst && !isAdmin) {
    return <Navigate to="/client" replace />
  }

  return <>{children}</>
}
